import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateArtifactContext } from "../../../../context/CreateArtifactContext";
import {
  SKArtifact,
  SKVideoArtifact,
  ContentState,
  getArtefactIcon,
  getContentCreatorActionableItems,
  ContentSubstate,
  TopicTypeString,
} from "../../../../models/SKArtifact";
import { useNavigationBar } from "../../../../context/NavigationContext";
import { capitalizeFirstLetter } from "../../../../utils/stringFunctions";
import { AddContentItem } from "./components/AddContentItem";
import * as Yup from "yup";
import { AddTextField } from "../../../../components/AddTextField";
import { AddTextAreaField } from "../../../../components/AddTextAreaField";
import ContentHistoryNavigator from "./components/ContentHistoryNavigator";
import { PublishingTier } from "./components/PublishingTier";
import ContextCheckListCard from "./components/ContextCheckListCard";
import { ArtifactContainerRef } from "./CreateArtifact";
import ContentEditNavigator from "../../../contentModeration/ContentEditNavigator";
import { useAuth, UserRole } from "../../../../context/AuthContext";
import ContentReportNavigator from "./components/ContentReportNavigator";

const VideoArtifactContainer = forwardRef<ArtifactContainerRef>((props, ref) => {
  const { contentData, updateContentData, artifactId, submit, containerButtonTitle, isEditable, isModerator, isContentEditing } =
    useContext(CreateArtifactContext);

  const context = useAuth();
  const [error, setErrorState] = useState<{ [key: string]: string | undefined }>({});

  const setErrors = (newErrors: { [key: string]: string | undefined }) => {
    setErrorState(newErrors);
  };

  useImperativeHandle(ref, () => ({
    containerSchema: () => schema,
    setErrors: newErrors => setErrors(newErrors),
  }));

  useEffect(() => {}, []);
  const navigate = useNavigate();
  const schema = Yup.object().shape({
    type: Yup.string().required(),
    title: Yup.string().required().max(50),
    subtitle: Yup.string().notRequired().max(500),
    text: Yup.string().notRequired().max(500),
    topic: Yup.string().required(),
    tier: Yup.string().required(),
    age: Yup.number().required(),
    status: Yup.string().required(),
    coverMedia: Yup.object({
      coverImageURL: Yup.string().required("Cover image is required"),
      coverVideoURL: Yup.string().notRequired(),
      coverAudioURL: Yup.string().notRequired(),
    }).required("Cover media is required"),
    certifyFirst: Yup.boolean().oneOf([true]).required("Please certify content"),
    certifySecond: Yup.boolean().oneOf([true]).required("Please certify content"),
    videoURL: Yup.string().required(),
  });

  // useEffect(() => {
  //   setNavProps({
  //     backArrow: true,
  //     title: "Create",
  //     subtitle: capitalizeFirstLetter(contentData.type ?? ""),
  //     icon: contentData.type ? getArtefactIcon(contentData.type) : undefined,
  //     buttonTitle: containerButtonTitle(),
  //     filterOption: false,
  //     enableLink: contentData.status === ContentState.IN_REVIEW ? false : isValidForm,
  //     getActionItems: () => getContentCreatorActionableItems(contentData.status, contentData.subStatus),
  //     onActionItemSelect: actionItem => {},
  //     onNext: submit,
  //     onClose: () => {
  //       navigate(-1);
  //     },
  //   });
  // }, [isValidForm, contentData]);

  // useEffect(() => {
  //   {
  //     contentData.status != ContentState.IN_REVIEW ? setIsEdit(false) : setIsEdit(true);
  //   }
  //   const validate = async () => {
  //     try {
  //       await schema.validate(contentData, { abortEarly: false });
  //       setIsValidForm(true);
  //     } catch (err) {
  //       setIsValidForm(false);
  //     }
  //   };
  //   validate();
  // }, [contentData, schema]);

  return (
    <>
      <div className="artifact-page-context">
        {isModerator &&
        !isContentEditing &&
        artifactId &&
        contentData.status === ContentState.ACTIVE &&
        contentData.subStatus === ContentSubstate.EDIT_IN_REVIEW ? (
          <ContentEditNavigator title="Channel Edit" docId={artifactId!} />
        ) : (
          ""
        )}

        {artifactId && (contentData as SKArtifact).hasHistory ? <ContentHistoryNavigator title="Channel Status" contentId={artifactId} /> : ""}
        {artifactId && context?.userRoles?.includes(UserRole.SkarvoAdmin) && contentData.reportCount && contentData.reportCount > 0 ? (
          <ContentReportNavigator title="Content Reports" />
        ) : (
          ""
        )}
        <AddContentItem
          index={1}
          onClick={() => {
            navigate("addVideo");
          }}
          count={(contentData as SKVideoArtifact)?.videoURL ? 1 : 0}
        />

        <AddTextField
          name={"addtitle"}
          title={"Add Title"}
          note={"Title can be the subject, purpose, theme or topic of your post."}
          placeholder={"Enter Title"}
          index={2}
          charAllowed={100}
          value={(contentData as SKArtifact).title}
          onChange={async e => {
            updateContentData({ title: e.target.value! });
          }}
          disabled={!isEditable}
        />

        <AddTextAreaField
          name={"addtext"}
          title={"Add Text"}
          note={"Maximum text length of 500 characters."}
          placeholder={"Enter Text"}
          index={3}
          height={150}
          charAllowed={500}
          value={(contentData as SKArtifact).subtitle as string}
          onChange={async e => {
            updateContentData({ subtitle: e.target.value! });
          }}
          disabled={!isEditable}
        />
        <ContextCheckListCard
          index={4}
          name="Add Topic"
          title="Choose the primary topic of your Content."
          purposeText="Add Topic"
          notes="Choose the topic that represents the primary focus of most of your Content."
          doneText={contentData.topic ? capitalizeFirstLetter(TopicTypeString[contentData.topic]) : undefined}
          action={() => {
            navigate("selectTopic");
          }}
          disabled={!isEditable}
        />

        <ContextCheckListCard
          index={5}
          name="Add Age Rating"
          title="Choose the Age Rating of your Content"
          purposeText="Add Age Rating"
          notes="Choose the age rating based on the appropriateness of your Content for children."
          doneText={contentData.age ? "Age " + contentData.age + "+" : undefined}
          action={() => {
            navigate("ageRating");
          }}
          disabled={!isEditable}
        />

        <ContextCheckListCard
          index={6}
          name="Certify Content"
          title="Certify Content"
          purposeText="Certify Content"
          notes="Certify that your Content would ensure and comply with these requirements."
          doneText={contentData.certifyFirst && contentData.certifySecond ? "Certified" : undefined}
          action={() => {
            navigate("certifyContent");
          }}
          disabled={!isEditable}
        />
        <PublishingTier index={7} />
      </div>
    </>
  );
});

export default VideoArtifactContainer;
